import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { chatAPI } from '../api/client';
import { COLORS } from '../utils/uiStyles';
import { parseServerUtc } from '../utils/kstTime';

// 서버가 link 를 안 준 알림(옛 행 포함)이 갈 유형별 기본 경로.
const TYPE_ROUTES = {
  inquiry_answered: '/inquiries',
  cs_answered: '/tickets',
  meeting_share: '/meetings',
  task_pm: '/tasks',
  devreq: '/dev-requests',
  budget_alert: '/admin',
  host_alert: '/admin',
  model_change: '/admin',
  limit_approved: '/',
  limit_rejected: '/',
  // 관리자 알림(수신자 = role 'admin' — alert_admins · internal_data/executor.py · deploy_prod.sh) → 관리자 화면 첫 탭.
  anthropic_key: '/admin',
  system_error: '/admin',
  feature_registry_error: '/admin',
  feature_registry_stale: '/admin',
  deploy_fail: '/admin',
};

// 🔴 서버가 준 경로는 앱 안 경로만 따른다 — '/' 로 시작하고 '//'·'/\'(다른 호스트로 해석된다)가 아니며
//    공백·탭·줄바꿈이 없어야 한다(URL 파서가 탭·줄바꿈을 지워 '/\t/evil' 이 '//evil' 이 된다).
//    제어문자 범위(\x00-\x1f)는 쓰지 않는다 — CRA eslint `no-control-regex` 가 경고를 낸다.
//    URL 파서가 지워 '//' 를 만드는 문자는 탭·LF·CR 뿐이고 셋 다 `\s` 가 막는다.
const safeLink = (value) => (
  typeof value === 'string' && value.length <= 300 && /^\/(?![/\\])[^\s]*$/.test(value) ? value : null
);

// 알림을 누르면 갈 경로 — 종 아이콘(여기)과 채팅 화면 팝업(ChatModals «보기»)이 같은 규칙을 쓴다.
// 서버가 실어 준 경로가 먼저다(업무 알림이면 그 업무 상세까지 간다) — 없거나 앱 밖이면 유형별 기본 경로, 둘 다 없으면 null.
export function notificationPath(n) {
  const type = n && n.type;
  const fallback = Object.prototype.hasOwnProperty.call(TYPE_ROUTES, type) ? TYPE_ROUTES[type] : null;
  return safeLink(n && n.link) || fallback;
}

function iconFor(type) {
  if (type === 'budget_alert') return '💰';
  if (type === 'inquiry_answered') return '❓';
  if (type === 'cs_answered') return '💬';
  if (type === 'meeting_share') return '📝';
  if (type === 'task_pm') return '📋';
  if (type === 'devreq') return '🛠️';
  if ((type || '').startsWith('limit_')) return '🔔';
  return '📢';
}

// 🔴 서버는 'Z' 없는 UTC naive 로 준다 → UTC 로 읽고 KST 로 그린다(2026-10-03).
//    시간대를 안 주면 브라우저 로컬(UTC 컨테이너·해외 접속)로 흘러 시각이 어긋난다.
function formatTime(value) {
  const d = parseServerUtc(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('ko-KR', {
    timeZone: 'Asia/Seoul',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

// 창 크기 — 기본 폭 360 · 높이 460 · 화면 가장자리·셸 끝에서 띄우는 여백 8 · 줄어도 지키는 최소 폭 200·높이 160.
const DIALOG_W = 360;
const DIALOG_MAX_H = 460;
const EDGE = 8;
const MIN_W = 200;
const MIN_H = 160;
// 문서 루트 CSS zoom(모바일 글자 크기) — getBoundingClientRect 와 innerWidth/innerHeight 는 둘 다 확대된 화면 px(Chrome 152 실측: zoom 1.5 에서 CSS top 100 → rect.top 150)라, 모두 zoom 으로 나눠 CSS px 로 맞춘다.
const rootZoom = () => {
  try {
    const z = parseFloat(getComputedStyle(document.documentElement).zoom);
    return Number.isFinite(z) && z > 0 ? z : 1;
  } catch (e) {
    return 1;
  }
};
const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// placement — 'above'(기본) = 단추 «위»로 자라는 창(bottom 기준) · 'below' = 단추 «아래»로 자라는 창(top 기준 · 사이드바 머리·접힘 단추).
//   창은 열릴 때(·창 크기가 바뀔 때) 자기 자리를 재서 «화면 밖/셸 밖으로 안 나가게» 폭·최대 높이를 줄인다(f8b2):
//     · 높이 — 'below' 는 창 위쪽 끝에서 화면 아래까지, 'above' 는 화면 위에서 창 아래쪽 끝까지. 목록(스크롤)이 남는 높이를 가져간다.
//     · 폭 — 분할 보기에서 주 패널이 좁으면(.gw-shell 이 패널 폭) 셸 오른쪽 끝 안쪽으로.
// modal — 창이 body 포털로 떠 aria-modal 서랍 «밖»에 놓일 때(서랍 모드 머리 알림) 켠다: aria-modal · 열면 창으로 초점 · Tab 가둠 · 닫으면 열기 전 초점으로.
//   🔴 aria-modal 은 이 «가둠»이 있을 때만 단다 — 사이드바에 붙은 일반 창은 덮개도 가둠도 없는 팝오버라 단추 아래에서 Tab 으로 바로 이어지고 뒤 화면도 쓸 수 있다.
export default function NotificationCenter({ open, compact = false, placement = 'above', modal = false, onClose, onUnreadChange }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const dialogRef = useRef(null);
  const [fit, setFit] = useState(null);   // { width, maxHeight } — 잰 값(없으면 CSS 기본값)

  const syncUnread = useCallback((rows) => {
    const count = rows.filter((n) => !n.is_read).length;
    if (onUnreadChange) onUnreadChange(count);
  }, [onUnreadChange]);

  const fetchNotifications = useCallback(() => {
    if (!open || !chatAPI.getNotifications) return Promise.resolve();
    setLoading(true);
    return chatAPI.getNotifications({ include_read: true })
      .then((r) => {
        const rows = Array.isArray(r.data) ? r.data : [];
        setItems(rows);
        syncUnread(rows);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, syncUnread]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return undefined;
    const iv = setInterval(fetchNotifications, 60000);
    return () => clearInterval(iv);
  }, [open, fetchNotifications]);

  useEffect(() => {
    if (!open) return undefined;
    // Escape 처리는 원래대로(window 버블 · preventDefault 없음) — 서랍(MobileDrawer)이 `data-gw-escape-layer`(아래 dialog 루트)를
    // 보고 비켜선다. 여기서 preventDefault 를 하면 다른 화면의 `defaultPrevented` 검사 처리기가 막힌다.
    const onKey = (e) => {
      if (e.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) { setFit(null); return undefined; }
    const measure = () => {
      const el = dialogRef.current;
      if (!el) return;
      const zoom = rootZoom();
      const r = el.getBoundingClientRect();
      const room = placement === 'below' ? (window.innerHeight - r.top) / zoom - EDGE : r.bottom / zoom - EDGE;
      const maxHeight = Math.round(Math.max(MIN_H, Math.min(DIALOG_MAX_H, room)));
      const shell = el.closest('.gw-shell');
      const sr = shell ? shell.getBoundingClientRect() : null;
      const right = (sr && sr.width > 0 ? Math.min(sr.right, window.innerWidth) : window.innerWidth) / zoom;
      const width = Math.round(Math.max(MIN_W, Math.min(DIALOG_W, window.innerWidth / zoom - 24, right - r.left / zoom - EDGE)));
      setFit((prev) => (prev && prev.width === width && prev.maxHeight === maxHeight ? prev : { width, maxHeight }));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [open, placement, compact]);

  useEffect(() => {
    if (!open || !modal) return undefined;
    const before = document.activeElement;
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      // 닫힌 뒤에도 초점이 body(창이 사라져 갈 곳을 잃음)일 때만 돌려준다 — 그새 사용자가 다른 곳으로 옮겼으면 뺏지 않는다.
      const now = document.activeElement;
      const lost = !now || now === document.body || !now.isConnected;
      if (lost && before && before !== document.body && before.isConnected && typeof before.focus === 'function') before.focus();
    };
  }, [open, modal]);

  if (!open) return null;

  const trapTab = (e) => {
    if (e.key !== 'Tab') return;
    const root = dialogRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll(FOCUSABLE));
    if (!nodes.length) { e.preventDefault(); root.focus(); return; }
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === root)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (at === last || at === root)) { e.preventDefault(); first.focus(); }
  };

  const markReadLocally = (id) => {
    setItems((prev) => {
      const next = prev.map((n) => (n.id === id ? { ...n, is_read: true } : n));
      syncUnread(next);
      return next;
    });
  };

  const handleItemClick = async (item) => {
    if (!item.is_read && chatAPI.readNotification) {
      await chatAPI.readNotification(item.id).catch(() => {});
      markReadLocally(item.id);
    }
    const path = notificationPath(item);
    if (path) {
      navigate(path);
      if (onClose) onClose();
    }
  };

  const handleReadAll = async () => {
    if (busy || !chatAPI.readAllNotifications) return;
    setBusy(true);
    await chatAPI.readAllNotifications().catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    if (onUnreadChange) onUnreadChange(0);
    setBusy(false);
  };

  const unreadCount = items.filter((n) => !n.is_read).length;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-label="알림 센터"
      aria-modal={modal ? 'true' : undefined}
      tabIndex={modal ? -1 : undefined}
      onKeyDown={modal ? trapTab : undefined}
      data-gw-escape-layer=""
      style={{
        position: 'absolute',
        left: compact ? '46px' : 0,
        ...(placement === 'below' ? { top: 0, bottom: 'auto' } : { bottom: compact ? 0 : '44px' }),
        width: fit ? `${fit.width}px` : 'min(360px, calc(100vw - 24px))',
        maxHeight: fit ? `${fit.maxHeight}px` : 'min(460px, calc(100dvh - 80px))',
        display: 'flex',
        flexDirection: 'column',
        background: COLORS.surface,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '8px',
        boxShadow: '0 18px 40px rgba(15,23,42,.22)',
        zIndex: 80,
        overflow: 'hidden',
        outline: 'none',
      }}
    >
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text }}>알림</div>
          <div style={{ fontSize: '12px', color: COLORS.textSub, marginTop: '2px' }}>미읽음 {unreadCount.toLocaleString()}개</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <button type="button" onClick={handleReadAll} disabled={busy || unreadCount === 0}
            style={{ border: `1px solid ${COLORS.border}`, background: unreadCount === 0 ? '#f9fafb' : COLORS.surface, color: unreadCount === 0 ? COLORS.textMuted : COLORS.primaryDark, borderRadius: '6px', padding: '6px 8px', fontSize: '12px', fontWeight: 700, cursor: unreadCount === 0 ? 'default' : 'pointer' }}>
            모두 읽음
          </button>
          <button type="button" onClick={onClose} aria-label="알림 닫기"
            style={{ width: '28px', height: '28px', border: 'none', borderRadius: '6px', background: 'transparent', color: COLORS.textSub, cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>
            ×
          </button>
        </div>
      </div>

      <div className="sb-scroll" style={{ flex: '1 1 auto', minHeight: 0, maxHeight: '360px', overflowY: 'auto' }}>
        {loading && items.length === 0 ? (
          <div style={{ padding: '18px 14px', fontSize: '13px', color: COLORS.textSub }}>불러오는 중입니다.</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '18px 14px', fontSize: '13px', color: COLORS.textSub }}>알림이 없습니다.</div>
        ) : items.map((item) => {
          const unread = !item.is_read;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => handleItemClick(item)}
              style={{
                width: '100%',
                display: 'flex',
                gap: '10px',
                padding: '12px 14px',
                border: 'none',
                borderBottom: `1px solid ${COLORS.borderSoft}`,
                background: unread ? COLORS.primarySoft : COLORS.surface,
                color: COLORS.text,
                textAlign: 'left',
                cursor: 'pointer',
              }}
            >
              <span aria-hidden="true" style={{ width: '24px', height: '24px', borderRadius: '6px', background: unread ? '#fff' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '15px' }}>{iconFor(item.type)}</span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: 'block', fontSize: '13px', fontWeight: unread ? 700 : 500, color: unread ? COLORS.selectedText : COLORS.text, lineHeight: 1.35 }}>{item.message}</span>
                <span style={{ display: 'block', marginTop: '5px', fontSize: '11px', color: COLORS.textMuted }}>{formatTime(item.created_at)}</span>
              </span>
              {unread && <span title="미읽음" style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', flexShrink: 0, marginTop: '7px' }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
